import React, { useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Home } from "./components/Home";
import SetupPassphrase from './components/SetupPassphrase';
import VerifyPassphrase from './components/VerifyPassphrase';
import Login from './components/Login';
import ThreadDetail from './components/ThreadDetail';
import { AccountSettings } from './components/AccountSettings';
import { UserProfile } from './components/UserProfile';
import { UsageDashboard } from './components/UsageDashboard';
import { MemberManagement } from './components/MemberManagement';
import { useAuth } from './contexts/AuthContext';
import { StorageHelper } from './utils/storageHelper';
import { SetupRequirement } from "./api/api-client";

// Protected route wrapper component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, masterKey, isInitialized } = useAuth();
  const [passphraseVerified, setPassphraseVerified] = useState(false);
  const location = useLocation();
  
  // If auth isn't initialized yet, return a loading state
  if (!isInitialized) {
    return <div className="text-center p-5">Loading...</div>;
  }
  
  // If user is not authenticated, redirect to home with return URL in state
  if (!isAuthenticated) {
    const returnTo = location.pathname + location.search;
    return <Navigate to="/" replace state={{ returnTo }} />;
  }
  
  // If user has completed setup but needs to enter passphrase
  if (!masterKey && !passphraseVerified) {
    // Check if we have the passphrase in storage
    const storedPassphrase = StorageHelper.getPassphrase();
    
    // If we have no passphrase, show the verify passphrase component
    if (!storedPassphrase) {
      return <VerifyPassphrase onVerified={() => setPassphraseVerified(true)} />;
    }
  }
  
  // All checks passed, render the protected content
  return <>{children}</>;
};

// Setup route wrapper component
export const SetupRoute = () => {
  const { isAuthenticated, setupNeeded, isInitialized, masterKey, refreshUser } = useAuth();
  
  // If auth isn't initialized yet, return a loading state
  if (!isInitialized) {
    return <div className="text-center p-5">Loading...</div>;
  }
  
  // If user is not authenticated, redirect to home
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  if ((setupNeeded & SetupRequirement.MasterKey) !== 0) {
     return <SetupPassphrase />;
  }

   if ((setupNeeded & SetupRequirement.MasterKey) === 0 && !masterKey) {
      return <VerifyPassphrase onVerified={() => {
         refreshUser();
      }} />;
   }


   if ((setupNeeded & SetupRequirement.ApiKey) !== 0) {
     return <AccountSettings />;
  }
  
  return <SetupPassphrase />;
};

const AppRoutes: React.FC = () => {
  const { isAuthenticated, setupNeeded, masterKey, isInitialized } = useAuth();

  // Add any additional routing logic here based on auth state
   // Check if we need to get a passphrase
   if (isInitialized && isAuthenticated && ((setupNeeded !== SetupRequirement.None) || !masterKey)) {
      return <SetupRoute />;
   }

   return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<Login />} />
      <Route path="/google-callback" element={<Login />} />

      {/* Setup route - authenticated but needs setup */}
      <Route path="/setup" element={<SetupRoute />} />

      {/* Protected routes - require full authentication */}
      <Route path="/threads/:id" element={<ProtectedRoute><ThreadDetail /></ProtectedRoute>} />
      <Route path="/account/settings" element={<ProtectedRoute><AccountSettings /></ProtectedRoute>} />
      <Route path="/account/profile" element={<ProtectedRoute><UserProfile /></ProtectedRoute>} />
      <Route path="/account/usage" element={<ProtectedRoute><UsageDashboard /></ProtectedRoute>} />
      <Route path="/account/members" element={<ProtectedRoute><MemberManagement /></ProtectedRoute>} />
      
      {/* Catch-all route for 404 */}
      <Route path="*" element={
        <div className="container mt-5 text-center">
          <h2>Page Not Found</h2>
          <p>The page you're looking for doesn't exist.</p>
        </div>
      } />
    </Routes>
  );
};

export default AppRoutes;